import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSpring, animated } from "@react-spring/web";
import { Globe, Loader2, Lock, Plus } from "lucide-react";
import { useCreateGitRepo, useSelectGitRepo, useGitConnection, useGitOrgs } from "@/hooks/git";
import type { CreateGitRepoInput } from "@/services/git/types";
import { CustomSelect } from "@/components/atoms/CustomSelect";

// ─── Create Repo Dropdown ─────────────────────────────────────────────────────

export function CreateRepoDropdown() {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [ownerLogin, setOwnerLogin] = useState("");
  const [isPrivate, setIsPrivate] = useState(true);
  const [initWithReadme, setInitWithReadme] = useState(true);

  const { data: orgs = [], isLoading: orgsLoading } = useGitOrgs();
  const { data: connection } = useGitConnection();
  const { mutate: createRepo, isPending, error, reset } = useCreateGitRepo();
  const { mutate: selectRepo } = useSelectGitRepo();

  const personalLogin = connection?.provider_login ?? "";
  const firstOwner = orgs[0]?.login ?? personalLogin;
  const effectiveOwner = ownerLogin || firstOwner;
  const ownerType: "org" | "user" = orgs.some((o) => o.login === effectiveOwner) ? "org" : "user";

  const ownerOptions = [
    ...(personalLogin ? [personalLogin] : []),
    ...orgs.map((o) => o.login).filter((l) => l !== personalLogin),
  ];

  const nameError =
    name && !/^[a-zA-Z0-9_.-]+$/.test(name)
      ? "Only letters, numbers, hyphens, underscores, and dots."
      : null;

  function handleOpen() {
    if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect());
    setOpen(true);
  }

  function handleClose() {
    setOpen(false);
    setName("");
    setDescription("");
    setOwnerLogin("");
    reset();
  }

  function handleToggle() {
    open ? handleClose() : handleOpen();
  }

  useEffect(() => {
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !dropdownRef.current?.contains(t)) handleClose();
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !effectiveOwner || nameError) return;
    const input: CreateGitRepoInput = {
      name: name.trim(),
      private: isPrivate,
      owner_login: effectiveOwner,
      owner_type: ownerType,
      description: description.trim() || undefined,
      auto_init: initWithReadme,
    };
    createRepo(input, {
      onSuccess: (repo) => {
        selectRepo(repo.id);
        handleClose();
      },
    });
  }

  const dropdownSpring = useSpring({
    opacity: open ? 1 : 0,
    transform: open ? "translateY(0px) scale(1)" : "translateY(-6px) scale(0.98)",
    config: { tension: 300, friction: 26, clamp: true },
  });

  return (
    <>
      <button ref={triggerRef} type="button" onClick={handleToggle} className="studio-button">
        <Plus className="h-4 w-4" />
        New repo
      </button>

      {createPortal(
        <animated.div
          ref={dropdownRef}
          style={{
            ...dropdownSpring,
            pointerEvents: open ? "auto" : "none",
            position: "fixed",
            top: rect ? rect.bottom + 10 : 0,
            right: rect ? window.innerWidth - rect.right : 0,
            width: 300,
            zIndex: 50,
          }}
          className="relative"
        >
          {/* Arrow pointing up */}
          <div
            className="absolute -top-[7px] right-4 h-3.5 w-3.5 rotate-45 rounded-tl-sm border-l border-t border-border bg-popover"
          />

          {/* Card */}
          <div className="overflow-hidden rounded-xl border border-border bg-popover shadow-[0_16px_48px_rgba(0,0,0,0.45)]">
            <form onSubmit={handleSubmit} className="p-4">

              <p className="mb-3 text-xs font-semibold text-foreground">Create repository</p>

              <div className="space-y-2.5">
                {/* Owner */}
                {orgsLoading ? (
                  <div className="h-8 animate-pulse rounded-lg border border-border bg-muted/40" />
                ) : ownerOptions.length > 1 ? (
                  <CustomSelect
                    value={effectiveOwner}
                    onChange={setOwnerLogin}
                    options={ownerOptions.map((login) => ({
                      value: login,
                      label: orgs.some((o) => o.login === login) ? login : `${login} (personal)`,
                    }))}
                  />
                ) : (
                  <div className="flex h-8 items-center rounded-lg border border-border/60 bg-muted/20 px-3 text-xs text-muted-foreground">
                    {effectiveOwner || "—"}
                  </div>
                )}

                {/* Name */}
                <div>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Repository name *"
                    autoFocus
                    required
                    className="w-full rounded-lg border border-input bg-background px-3 py-1.5 text-xs outline-none transition-colors focus:border-primary/60"
                  />
                  {nameError && (
                    <p className="mt-1 text-[10px] text-red-400">{nameError}</p>
                  )}
                </div>

                {/* Description */}
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description (optional)"
                  className="w-full rounded-lg border border-input bg-background px-3 py-1.5 text-xs outline-none transition-colors focus:border-primary/60"
                />

                {/* Visibility — segmented control */}
                <div className="flex overflow-hidden rounded-lg border border-border/60">
                  <button
                    type="button"
                    onClick={() => setIsPrivate(true)}
                    className={[
                      "flex flex-1 items-center justify-center gap-1.5 py-1.5 text-xs font-medium transition-colors",
                      isPrivate
                        ? "bg-primary/15 text-primary"
                        : "text-muted-foreground hover:bg-muted/30",
                    ].join(" ")}
                  >
                    <Lock className="h-3 w-3" />
                    Private
                  </button>
                  <div className="w-px bg-border/60" />
                  <button
                    type="button"
                    onClick={() => setIsPrivate(false)}
                    className={[
                      "flex flex-1 items-center justify-center gap-1.5 py-1.5 text-xs font-medium transition-colors",
                      !isPrivate
                        ? "bg-primary/15 text-primary"
                        : "text-muted-foreground hover:bg-muted/30",
                    ].join(" ")}
                  >
                    <Globe className="h-3 w-3" />
                    Public
                  </button>
                </div>

                {/* README */}
                <label className="flex cursor-pointer items-center gap-2 text-xs text-foreground">
                  <input
                    type="checkbox"
                    checked={initWithReadme}
                    onChange={(e) => setInitWithReadme(e.target.checked)}
                    className="h-3.5 w-3.5 accent-primary"
                  />
                  Initialize with README
                </label>

                {error && (
                  <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-[10px] text-red-400">
                    {error.message}
                  </p>
                )}

                {/* Actions — slim text-style buttons */}
                <div className="flex items-center justify-end gap-3 border-t border-border/50 pt-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!name.trim() || !effectiveOwner || !!nameError || isPending}
                    className="flex items-center gap-1 rounded-lg bg-primary px-3 py-1 text-xs font-medium text-primary-foreground transition-opacity disabled:opacity-40 hover:opacity-90"
                  >
                    {isPending
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : <Plus className="h-3 w-3" />}
                    {isPending ? "Creating…" : "Create"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </animated.div>,
        document.body,
      )}
    </>
  );
}
